import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TaskTable from './TaskTable';
import { PageLayout } from './TaskManager.styles';
import Header from './Header';
import TaskTableSummary from './TaskTableSummary';
import { Task } from './types';
import { useTaskManagerContext } from './TaskManagerContext';
import TaskSidebar from './task-sidebar/TaskSidebar';
import { WrappedSpinner } from 'spinner';
import TaskFilters from './TaskFilters';
import { usePaginatedTasks } from './usePaginatedTasks';
import ErrorState from './ErrorState';

type TaskManagerProps = {
  isBpaView?: boolean;
  requireTeam?: boolean;
};

const TaskManager = ({ isBpaView = false, requireTeam = false }: TaskManagerProps) => {
  const location = useLocation();
  const { assignTask, loggedInPerson } = useTaskManagerContext();

  const { tasks, error, loading, getNextPage, getPrevPage, total } = usePaginatedTasks({
    isBpaView,
    requireTeam,
  });

  const [selectedTask, setSelectedTask] = useState<Task>();
  const tableRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (!location.search) return;
    setSelectedTask(undefined);
  }, [location.search]);

  const handleAssignTask = async (task: Task) => {
    const res = await assignTask(task);
    return res?.data as Record<string, any>;
  };

  const handleRowClick = (task: Task) => {
    if (task.id === selectedTask?.id) {
      setSelectedTask(undefined);
      return;
    }
    setSelectedTask(task);
  };

  if (error && !tasks) {
    return (
      <PageLayout>
        <Header loggedInPerson={loggedInPerson} isBpaView={isBpaView} />
        <TaskFilters isBpaView={isBpaView} requireTeam={requireTeam} />
        <ErrorState
          description={error.message}
          title="Uh oh! The following error was encountered while fetching the data for this page."
        />
      </PageLayout>
    );
  }

  const selectedTaskFromDataToKeepCurrentAfterUpdates =
    selectedTask && tasks?.find(task => task.id === selectedTask.id);

  return (
    <PageLayout>
      <Header loggedInPerson={loggedInPerson} isBpaView={isBpaView} />
      <TaskFilters isBpaView={isBpaView} requireTeam={requireTeam} />
      {loading && !tasks ? (
        <WrappedSpinner></WrappedSpinner>
      ) : (
        <>
          <TaskTableSummary total={total} textSize="14px" />
          <TaskTable
            tasks={tasks || []}
            paginationFns={{ getNextPage, getPrevPage }}
            assignTask={handleAssignTask}
            isBpaView={isBpaView}
            onRowClick={handleRowClick}
            ref={tableRef}
          />
          {selectedTaskFromDataToKeepCurrentAfterUpdates ? (
            <TaskSidebar
              task={selectedTaskFromDataToKeepCurrentAfterUpdates}
              dismissSidebar={() => setSelectedTask(undefined)}
              preventDismiss={event =>
                tableRef.current && event.composedPath().includes(tableRef.current)
              }
              assignTask={handleAssignTask}
            />
          ) : null}
        </>
      )}
    </PageLayout>
  );
};

export default TaskManager;
